// ============================================================
// Süper admin paneli — uygulama sahibinin ekranı
//
// Kurum yöneticileri bu ekranı GÖREMEZ. Kiracıların üstündeki
// katmandır: gelir, kullanım, kayıp riski, operasyonel destek.
//
// Kişisel veri sınırı: bu panelde çocuk adı, sağlık verisi veya veli
// bilgisi GÖSTERİLMEZ. Operasyonel riskler sayı ve kategori olarak
// döner. Uygulama sahibinin müşterinin çocuk verisine erişmesi için
// meşru bir gerekçe yok — destek vermek için sayı yeterli.
//
// Bu ilke artık YALNIZCA bu ekranda değil, sistem genelinde geçerli:
// sistem rolü kurum ekranlarının hiçbirinde yok (domain/auth.js →
// GORUNUM_YETKISI) ve Firestore kuralları kiracı ağacına sistemi hiç
// sokmuyor (firestore.rules → kiraciDa). Bir süre panelde veri
// gizlenirken kenar çubuğu Öğrenciler ve Sağlık ekranlarını açıyordu;
// o çelişki kapatıldı.
//
// Bedeli bilinçli: bir kreş "şu faturada hata var" dediğinde ekranı
// göremiyoruz, ekran görüntüsü istiyoruz.
// ============================================================

import { card, emptyNote, sectionTitle } from "../components/ui.js";
import { DONEM } from "../config.js";
import { esc, fmtDate, money } from "../lib/fmt.js";
import { yapabilir } from "../domain/auth.js";
import { PLANLAR, PLAN_SIRASI } from "../domain/billing.js";
import { basvuruSekmesi } from "./sistemBasvuru.js";
import {
  gelirOzeti,
  kullanimOzeti,
  operasyonGorunur,
  operasyonelRiskler,
  riskSatirlari,
  sistemDenetimIzi,
  takvim,
  yukseltmeFirsatlari,
} from "../domain/sistem.js";
import { state } from "../state.js";

const SEKMELER = [
  { id: "ozet", label: "Genel Bakış" },
  { id: "kiracilar", label: "Kiracılar" },
  { id: "gelir", label: "Gelir" },
  { id: "kullanim", label: "Kullanım" },
  { id: "operasyon", label: "Operasyon" },
  { id: "basvurular", label: "Başvurular" },
  { id: "denetim", label: "Denetim" },
];

const DURUM = {
  aktif:  { label: "Aktif",  chip: "bg-teal-soft text-teal" },
  deneme: { label: "Deneme", chip: "bg-sun-soft text-[#B07E14]" },
  askida: { label: "Askıda", chip: "bg-coral-soft text-coral" },
  iptal:  { label: "İptal",  chip: "bg-coral text-white" },
};

const RISK = {
  yuksek: { label: "Yüksek", chip: "bg-coral text-white", renk: "coral" },
  orta:   { label: "Orta",   chip: "bg-sun-soft text-[#B07E14]", renk: "sun" },
  dusuk:  { label: "Düşük",  chip: "bg-teal-soft text-teal", renk: "teal" },
};

export function viewSistem() {
  const aktif = state.sistemTab ?? "ozet";
  const govde = {
    ozet: ozetSekmesi,
    kiracilar: kiracilarSekmesi,
    gelir: gelirSekmesi,
    kullanim: kullanimSekmesi,
    operasyon: operasyonSekmesi,
    basvurular: basvuruSekmesi,
    denetim: denetimSekmesi,
  }[aktif] ?? ozetSekmesi;

  return `
  <div class="flex items-center gap-1 rounded-xl bg-line/50 p-1 mb-4 w-fit overflow-x-auto">
    ${SEKMELER.map(s => `
      <button data-sistemtab="${s.id}"
        class="rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition ${aktif === s.id ? "bg-white shadow-card" : "text-inksoft hover:text-ink"}">
        ${s.label}
      </button>`).join("")}
  </div>
  ${govde()}`;
}

/**
 * "Göremiyorum" ekranı. "Temiz" ile karıştırılmamalı — biri veriye
 * bakıp sorun bulamamak, diğeri veriye hiç bakamamak.
 */
const gorunmuyorNotu = () => card(`
  <div class="p-6">
    <h2 class="font-display font-bold text-base mb-1">Operasyonel veri görünmüyor</h2>
    <p class="text-sm text-inksoft leading-relaxed">
      Süresi dolmuş belge, sorunlu ilaç kaydı ve eksik KVKK rızası gibi
      uyarılar kiracının kendi kayıtlarından türetiliyor. Sistem operatörü
      bu kayıtlara erişmiyor, dolayısıyla burada <strong>sorun olmadığı
      değil, bakılamadığı</strong> yazıyor.
    </p>
    <p class="text-[11px] text-inksoft/80 mt-3 leading-relaxed">
      Bu uyarıların sistem panelinde görünebilmesi için sunucu tarafında
      sayı olarak toplanması gerekiyor — kiracı kaydındaki
      <code>usage</code> toplamlarının yaptığı gibi. Planlanan iş.
    </p>
  </div>`);

const kutu = (label, value, sub, color) => card(`
  <div class="p-4">
    <div class="text-xs font-semibold text-inksoft">${label}</div>
    <div class="font-display font-extrabold text-2xl mt-1 text-${color} break-words">${value}</div>
    <div class="text-[11px] text-inksoft/80 mt-0.5">${sub}</div>
  </div>`, "min-w-0");

// ============================================================
// Genel Bakış
// ============================================================
function ozetSekmesi() {
  const g = gelirOzeti();
  const k = kullanimOzeti();
  const risk = riskSatirlari();
  const ops = operasyonelRiskler();
  const yaklasan = takvim(30);
  const opsToplam = ops.reduce((a, x) => a + x.toplam, 0);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${kutu("MRR", money(g.mrr), `ARR ${money(g.arr)}`, "teal")}
    ${kutu("Kiracı", g.kiraciAdet, `${g.odeyen} ödeyen · ${g.denemede} deneme`, "grape")}
    ${kutu("Gecikmiş", money(g.gecikmisToplam), g.gecikmisToplam ? "tahsilat gerekiyor" : "gecikme yok", g.gecikmisToplam ? "coral" : "sky")}
    ${kutu("Yüksek Risk", risk.filter(x => x.seviye === "yuksek").length, "kiracı takip gerektiriyor", "coral")}
  </div>

  <div class="grid gap-4 lg:grid-cols-2 items-start">
    ${card(`
      <div class="p-5">
        ${sectionTitle("Plan Dağılımı")}
        ${g.planDagilimi.map(x => {
          const oran = g.mrr ? Math.round((x.mrr / g.mrr) * 100) : 0;
          return `
          <div class="mb-2.5">
            <div class="flex items-center justify-between gap-2 text-sm">
              <span>${esc(x.plan.label)} <span class="text-[11px] text-inksoft">${x.adet} kiracı</span></span>
              <span class="shrink-0 font-semibold">${money(x.mrr)} <span class="text-[11px] text-inksoft">%${oran}</span></span>
            </div>
            <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
              <div class="h-full rounded-full bg-grape" style="width:${oran}%"></div>
            </div>
          </div>`;
        }).join("")}
        <div class="text-[11px] text-inksoft mt-3 pt-3 border-t border-line">
          Ortalama kiracı geliri ${money(g.ortalamaGelir)} · deneme aboneliği MRR'a sayılmaz
        </div>
      </div>`, "min-w-0")}

    ${card(`
      <div class="p-5">
        ${sectionTitle("Yaklaşan (30 gün)", `<span class="text-[11px] text-inksoft">${yaklasan.length} olay</span>`)}
        ${yaklasan.length ? `
          <ul class="space-y-2">
            ${yaklasan.map(o => `
              <li class="flex items-center gap-3 rounded-xl border ${o.kalanGun <= 7 ? "border-coral" : "border-line"} px-3 py-2">
                <span class="w-12 shrink-0 text-center">
                  <span class="block font-display font-bold text-sm ${o.kalanGun <= 7 ? "text-coral" : ""}">${o.kalanGun}</span>
                  <span class="block text-[10px] text-inksoft">gün</span>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold truncate">${esc(o.kiraci.name)}</span>
                  <span class="block text-[11px] text-inksoft">
                    ${o.tip === "deneme" ? "Deneme bitiyor" : "Abonelik yenileniyor"} · ${fmtDate(o.tarih)}
                  </span>
                </span>
                <span class="shrink-0 text-sm font-semibold">${money(o.tutar)}</span>
              </li>`).join("")}
          </ul>` : emptyNote("30 gün içinde yenileme veya deneme bitişi yok.")}
      </div>`, "min-w-0")}

    ${card(`
      <div class="p-5">
        ${sectionTitle("Kayıp Riski", `<span class="text-[11px] text-inksoft hidden sm:block">Ürün verisinden türetilir</span>`)}
        <ul class="space-y-2">
          ${risk.slice(0, 5).map(x => `
            <li class="rounded-xl border border-line px-3 py-2">
              <div class="flex items-center gap-2">
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold truncate">${esc(x.kiraci.name)}</span>
                  <span class="block text-[11px] text-inksoft truncate">
                    ${x.sebepler.length ? x.sebepler.map(esc).join(" · ") : "Belirgin risk yok"}
                  </span>
                </span>
                <span class="text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${RISK[x.seviye].chip}">
                  ${RISK[x.seviye].label} ${x.puan}
                </span>
              </div>
            </li>`).join("")}
        </ul>
      </div>`, "min-w-0")}

    ${card(`
      <div class="p-5">
        ${sectionTitle("Operasyonel Destek", !operasyonGorunur()
          ? `<span class="text-[11px] font-bold rounded-full bg-line text-inksoft px-2.5 py-1">Görünmüyor</span>`
          : opsToplam
          ? `<span class="text-[11px] font-bold rounded-full bg-coral text-white px-2.5 py-1">${opsToplam} konu</span>`
          : `<span class="text-[11px] font-bold rounded-full bg-teal-soft text-teal px-2.5 py-1">Temiz</span>`)}
        <p class="text-[11px] text-inksoft mb-2">
          Müşteriyi uyarmayı gerektiren durumlar. Kişisel veri gösterilmez.
        </p>
        <div class="grid grid-cols-2 gap-2 text-sm">
          ${[
            ["Süresi dolmuş zorunlu belge", ops.reduce((a, x) => a + x.belgeDolmus, 0)],
            ["Sorunlu ilaç kaydı", ops.reduce((a, x) => a + x.ilacSorunlu, 0)],
            ["Veliye bildirilmemiş olay", ops.reduce((a, x) => a + x.olayBildirilmemis, 0)],
            ["Eksik KVKK rızası", ops.reduce((a, x) => a + x.rizaEksik, 0)],
          ].map(([ad, sayi]) => `
            <div class="rounded-xl ${sayi ? "bg-coral-soft" : "bg-paper"} p-3">
              <div class="font-display font-bold text-xl ${sayi ? "text-coral" : "text-inksoft"}">${sayi}</div>
              <div class="text-[11px] text-inksoft leading-tight">${ad}</div>
            </div>`).join("")}
        </div>
      </div>`, "min-w-0")}

    ${kullanimOzetKarti(k)}
  </div>`;
}

function kullanimOzetKarti(k) {
  return card(`
    <div class="p-5">
      ${sectionTitle("Sistem Büyüklüğü")}
      <div class="grid grid-cols-4 gap-2 text-center">
        ${[["Kiracı", k.satirlar.length], ["Şube", k.sube], ["Öğrenci", k.ogrenci], ["Personel", k.personel]]
          .map(([ad, s]) => `
          <div class="rounded-xl bg-paper p-3">
            <div class="font-display font-bold text-xl">${s}</div>
            <div class="text-[11px] text-inksoft">${ad}</div>
          </div>`).join("")}
      </div>
      ${k.limitAsan ? `
        <div class="rounded-xl bg-sun-soft text-[#B07E14] text-xs font-semibold p-3 mt-3">
          ${k.limitAsan} kiracı plan limitini aşmış — yükseltme fırsatı.
        </div>` : ""}
    </div>`, "min-w-0 lg:col-span-2");
}

// ============================================================
// Kiracılar
// ============================================================
function kiracilarSekmesi() {
  const g = gelirOzeti();
  const duzenleyebilir = yapabilir("planDegistir");

  return `
  <div class="grid gap-4 items-start">
    ${g.satirlar.map(x => kiraciKarti(x, duzenleyebilir)).join("")}
  </div>

  <p class="text-[11px] text-inksoft mt-4">
    Kiracı verileri birbirinden yalıtılmıştır. "Görüntüle" bir kiracının
    ekranlarına geçmenizi sağlar; plan kapısına takılmazsınız çünkü
    sistem operatörü destek verebilmelidir.
  </p>`;
}

function kiraciKarti(x, duzenleyebilir) {
  const k = x.kiraci;
  const a = x.abonelik;
  const aktifGoruntu = k.id === state.tenantId;
  const d = DURUM[a.status] ?? DURUM.aktif;

  return card(`
    <div class="p-5">
      <div class="flex items-start gap-3">
        <span class="w-10 h-10 rounded-2xl grid place-items-center text-lg shrink-0
          ${aktifGoruntu ? "bg-teal-soft" : "bg-paper border border-line"}">🏫</span>

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <h2 class="font-display font-bold text-lg truncate">${esc(k.name)}</h2>
            <span class="text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${d.chip}">
              ${d.label}${x.denemeKalan != null ? ` · ${x.denemeKalan} gün` : ""}
            </span>
            <span class="text-[10px] font-bold rounded-full bg-grape-soft text-grape px-2 py-0.5 shrink-0">${esc(x.plan.label)}</span>
            <span class="text-[10px] font-bold rounded-full bg-paper border border-line px-2 py-0.5 shrink-0">${money(x.ucret.net)}/ay</span>
            ${x.gecikmisTutar ? `<span class="text-[10px] font-bold rounded-full bg-coral text-white px-2 py-0.5 shrink-0">${money(x.gecikmisTutar)} gecikmiş</span>` : ""}
            ${aktifGoruntu ? `<span class="text-[10px] font-bold rounded-full bg-ink text-white px-2 py-0.5 shrink-0">Görüntülenen</span>` : ""}
          </div>
          <div class="text-[11px] text-inksoft mt-0.5">
            ${esc(k.contact.name)} · ${esc(k.contact.email)} · ${esc(k.contact.phone)}
          </div>
          <div class="text-[11px] text-inksoft">
            ${fmtDate(a.since)} tarihinden beri müşteri ·
            yenileme ${fmtDate(a.renewsAt)} ·
            ${a.billingCycle === "yillik" ? "yıllık" : "aylık"} ödeme
          </div>
        </div>

        ${!aktifGoruntu ? `
          <button data-kiraci="${k.id}"
            class="rounded-xl border border-line bg-white text-xs font-semibold px-3 py-1.5 hover:bg-paper shrink-0">
            Görüntüle
          </button>` : ""}
      </div>

      <div class="grid grid-cols-3 gap-2 mt-4 text-center">
        ${[["Şube", k.branches.length], ["Öğrenci", x.kiraci.branches.reduce((s, b) => s + b.students.length, 0)],
           ["Açık bakiye", money(x.acikTutar)]].map(([ad, v]) => `
          <div class="rounded-xl bg-paper p-2.5">
            <div class="font-display font-bold text-sm">${v}</div>
            <div class="text-[10px] text-inksoft">${ad}</div>
          </div>`).join("")}
      </div>

      ${duzenleyebilir ? `
        <div class="flex items-center gap-2 mt-4 pt-3 border-t border-line flex-wrap">
          <span class="text-[11px] font-bold text-inksoft uppercase">Plan</span>
          ${PLAN_SIRASI.map(id => `
            <button data-planset="${k.id}:${id}"
              class="rounded-lg px-2.5 py-1 text-[11px] font-semibold transition
                ${id === x.plan.id ? "bg-grape text-white" : "border border-line bg-white hover:bg-paper"}">
              ${esc(PLANLAR[id].label)}
            </button>`).join("")}

          <span class="text-[11px] font-bold text-inksoft uppercase ml-2">Durum</span>
          ${["aktif", "deneme", "askida"].map(s => `
            <button data-statusset="${k.id}:${s}"
              class="rounded-lg px-2.5 py-1 text-[11px] font-semibold transition
                ${s === a.status ? "bg-ink text-white" : "border border-line bg-white hover:bg-paper"}">
              ${DURUM[s].label}
            </button>`).join("")}
        </div>
        <p class="text-[11px] text-inksoft mt-2">
          Değişiklik denetim izine kim/ne zaman olarak yazılır. Plan düşürüldüğünde
          o kiracının kullanıcılarının modül erişimi kapanır.
        </p>` : ""}
    </div>`, "min-w-0");
}

// ============================================================
// Gelir
// ============================================================
function gelirSekmesi() {
  const g = gelirOzeti();

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${kutu("MRR", money(g.mrr), `${g.odeyen} ödeyen kiracı`, "teal")}
    ${kutu("ARR", money(g.arr), "yıllık yinelenen", "grape")}
    ${kutu("Açık Bakiye", money(g.acikToplam), `${money(g.gecikmisToplam)} gecikmiş`, g.gecikmisToplam ? "coral" : "sky")}
    ${kutu("Ortalama Gelir", money(g.ortalamaGelir), "ödeyen kiracı başına", "sun")}
  </div>

  ${card(`
    <div class="p-5">
      ${sectionTitle("Kiracı Bazlı Gelir", `<span class="text-[11px] text-inksoft">${donemAdiGuvenli()}</span>`)}
      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[720px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
            <th class="py-2.5 pr-3">Kiracı</th>
            <th class="py-2.5 pr-3">Plan</th>
            <th class="py-2.5 pr-3">Durum</th>
            <th class="py-2.5 pr-3 text-right">Aylık</th>
            <th class="py-2.5 pr-3 text-right">MRR</th>
            <th class="py-2.5 pr-3 text-right">Açık</th>
            <th class="py-2.5 text-right">Yenileme</th>
          </tr></thead>
          <tbody class="divide-y divide-line/70">
            ${g.satirlar.map(x => `
              <tr>
                <td class="py-2.5 pr-3 font-semibold truncate max-w-[200px]">${esc(x.kiraci.name)}</td>
                <td class="py-2.5 pr-3 text-inksoft">${esc(x.plan.label)}</td>
                <td class="py-2.5 pr-3">
                  <span class="text-[10px] font-bold rounded-full px-2 py-0.5 ${(DURUM[x.abonelik.status] ?? DURUM.aktif).chip}">
                    ${(DURUM[x.abonelik.status] ?? DURUM.aktif).label}
                  </span>
                </td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(x.ucret.net)}</td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap font-semibold ${x.mrr ? "text-teal" : "text-inksoft"}">${money(x.mrr)}</td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap ${x.gecikmisTutar ? "text-coral font-semibold" : "text-inksoft"}">${money(x.acikTutar)}</td>
                <td class="py-2.5 text-right whitespace-nowrap text-inksoft">
                  ${x.yenilemeyeKalan != null ? `${x.yenilemeyeKalan} gün` : "—"}
                </td>
              </tr>`).join("")}
          </tbody>
          <tfoot><tr class="border-t-2 border-line font-semibold">
            <td class="py-2.5 pr-3" colspan="4">Toplam</td>
            <td class="py-2.5 pr-3 text-right whitespace-nowrap text-teal">${money(g.mrr)}</td>
            <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(g.acikToplam)}</td>
            <td></td>
          </tr></tfoot>
        </table>
      </div>
    </div>`)}`;
}

const donemAdiGuvenli = () => DONEM;

// ============================================================
// Kullanım
// ============================================================
function kullanimSekmesi() {
  const k = kullanimOzeti();
  const firsatlar = yukseltmeFirsatlari();

  // Şube kırılımı yalnızca kiracı ağacı yüklüyse var. Sistem
  // operatöründe yok: kiracı altındaki koleksiyonları okuyamıyor
  // (kişisel veri sınırı). Sütunları boş bırakmak yerine hiç
  // göstermiyoruz — boş hücre "veri yok" gibi okunur, oysa doğrusu
  // "göremiyoruz". Toplamlar kiracı belgesindeki usage'dan geliyor.
  const detay = k.satirlar.some(x => x.subeler.length > 0);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${kutu("Şube", k.sube, "sistem geneli", "sky")}
    ${kutu("Öğrenci", k.ogrenci, "sistem geneli", "teal")}
    ${kutu("Personel", k.personel, "sistem geneli", "grape")}
    ${kutu("Limit Aşan", k.limitAsan, "kiracı — yükseltme fırsatı", k.limitAsan ? "coral" : "sun")}
  </div>

  ${firsatlar.length ? card(`
    <div class="p-5">
      ${sectionTitle("Plan Yükseltme Fırsatları", `<span class="text-[11px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2.5 py-1">${firsatlar.length}</span>`)}
      <ul class="space-y-2">
        ${firsatlar.map(f => `
          <li class="flex items-center gap-3 rounded-xl border ${f.limit.asildi ? "border-coral" : "border-line"} px-3 py-2">
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-semibold truncate">${esc(f.kiraci.name)}</span>
              <span class="block text-[11px] text-inksoft">
                ${esc(f.plan.label)} planı · ${f.limit.alan} ${f.limit.olan}/${f.limit.limit}
                ${f.limit.asildi ? " — sınır aşıldı" : " — sınıra yaklaşıyor"}
              </span>
            </span>
            <span class="shrink-0 text-sm font-semibold">${money(f.ucret.net)}/ay</span>
          </li>`).join("")}
      </ul>
    </div>`, "mb-4") : ""}

  ${card(`
    <div class="p-5">
      ${sectionTitle("Kiracı ve Şube Dağılımı")}
      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[680px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
            <th class="py-2.5 pr-3">Kiracı / Şube</th>
            <th class="py-2.5 pr-3 text-right">Öğrenci</th>
            ${detay ? `<th class="py-2.5 pr-3 text-right">Sınıf</th>` : ""}
            <th class="py-2.5 pr-3 text-right">Personel</th>
            ${detay ? `<th class="py-2.5 text-right">Dönem net</th>` : ""}
          </tr></thead>
          <tbody class="divide-y divide-line/70">
            ${k.satirlar.flatMap(x => [
              `<tr class="bg-paper/60">
                <td class="py-2 pr-3 font-semibold">${esc(x.kiraci.name)}
                  <span class="text-[11px] text-inksoft font-normal">${esc(x.plan.label)}</span></td>
                <td class="py-2 pr-3 text-right font-semibold">${x.kullanim.ogrenci}</td>
                ${detay ? `<td class="py-2 pr-3 text-right text-inksoft">—</td>` : ""}
                <td class="py-2 pr-3 text-right font-semibold">${x.kullanim.personel}</td>
                ${detay ? `<td class="py-2 text-right text-inksoft">—</td>` : ""}
              </tr>`,
              ...x.subeler.map(b => `
                <tr>
                  <td class="py-2 pr-3 pl-6 text-inksoft truncate max-w-[240px]">${esc(b.sube.name)}</td>
                  <td class="py-2 pr-3 text-right">${b.ogrenci}</td>
                  <td class="py-2 pr-3 text-right text-inksoft">${b.sinif}</td>
                  <td class="py-2 pr-3 text-right text-inksoft">${b.personel}</td>
                  <td class="py-2 text-right whitespace-nowrap ${b.sonuc.net >= 0 ? "text-teal" : "text-coral"}">${money(b.sonuc.net)}</td>
                </tr>`),
            ]).join("")}
          </tbody>
        </table>
      </div>
      <p class="text-[11px] text-inksoft mt-3">
        ${detay
          ? `"Dönem net" kurumun kendi gelir–gider sonucudur; abonelik ücretiyle ilgisi yoktur.`
          : `Şube kırılımı ve kurumun gelir–gider sonucu burada <strong>gösterilmez</strong>:
             sistem operatörü kiracıların kayıtlarına erişmez. Yukarıdaki sayılar
             kiracı kaydındaki toplamlardan gelir.`}
      </p>
    </div>`)}`;
}

// ============================================================
// Operasyon
// ============================================================
function operasyonSekmesi() {
  if (!operasyonGorunur()) return gorunmuyorNotu();

  const ops = operasyonelRiskler();

  return `
  <div class="rounded-xl bg-paper border border-line p-4 mb-4">
    <div class="text-sm font-semibold">Kişisel veri sınırı</div>
    <p class="text-[11px] text-inksoft mt-1">
      Bu sekmede çocuk adı, sağlık verisi veya veli bilgisi gösterilmez —
      yalnızca sayı ve kategori. Uygulama sahibinin müşterinin çocuk
      verisine erişmesi için meşru bir gerekçe yok; destek vermek için
      sayı yeterli. Ayrıntı gerekiyorsa kurum yöneticisiyle iletişime geçilir.
    </p>
  </div>

  <div class="grid gap-4 items-start">
    ${ops.map(x => card(`
      <div class="p-5">
        <div class="flex items-center gap-2 flex-wrap">
          <h2 class="font-display font-bold truncate">${esc(x.kiraci.name)}</h2>
          ${x.toplam
            ? `<span class="text-[10px] font-bold rounded-full bg-coral text-white px-2 py-0.5">${x.toplam} konu</span>`
            : `<span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5">Temiz</span>`}
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          ${[
            ["Süresi dolmuş zorunlu belge", x.belgeDolmus],
            ["Sorunlu ilaç kaydı", x.ilacSorunlu],
            ["Bildirilmemiş olay", x.olayBildirilmemis],
            ["Eksik KVKK rızası", x.rizaEksik],
          ].map(([ad, sayi]) => `
            <div class="rounded-xl ${sayi ? "bg-coral-soft" : "bg-paper"} p-3">
              <div class="font-display font-bold text-lg ${sayi ? "text-coral" : "text-inksoft"}">${sayi}</div>
              <div class="text-[10px] text-inksoft leading-tight">${ad}</div>
            </div>`).join("")}
        </div>

        ${x.subeRiskleri.some(s => s.belgeDolmus || s.ilacSorunlu || s.olayBildirilmemis || s.rizaEksik) ? `
          <div class="mt-3 pt-3 border-t border-line">
            <div class="text-[11px] font-bold text-inksoft uppercase mb-1.5">Şube kırılımı</div>
            ${x.subeRiskleri.filter(s => s.belgeDolmus || s.ilacSorunlu || s.olayBildirilmemis || s.rizaEksik)
              .map(s => `
              <div class="flex items-center justify-between gap-2 text-[11px] py-0.5">
                <span class="truncate">${esc(s.sube.name)}</span>
                <span class="shrink-0 text-inksoft">
                  ${[s.belgeDolmus && `${s.belgeDolmus} belge`, s.ilacSorunlu && `${s.ilacSorunlu} ilaç`,
                     s.olayBildirilmemis && `${s.olayBildirilmemis} olay`, s.rizaEksik && `${s.rizaEksik} rıza`]
                    .filter(Boolean).join(" · ")}
                </span>
              </div>`).join("")}
          </div>` : ""}
      </div>`, "min-w-0")).join("")}
  </div>`;
}

// ============================================================
// Denetim
// ============================================================
function denetimSekmesi() {
  const kayitlar = sistemDenetimIzi(60);

  return card(`
    <div class="p-5">
      ${sectionTitle("Sistem Geneli Denetim İzi", `<span class="text-[11px] text-inksoft">son ${kayitlar.length} kayıt</span>`)}
      <p class="text-[11px] text-inksoft mb-3">
        Tüm kiracılardaki değişiklikler. Kayıtlar değiştirilemez ve silinemez.
      </p>

      ${kayitlar.length ? `
        <ol class="space-y-1.5">
          ${kayitlar.map(m => `
            <li class="flex items-start gap-3 rounded-xl border border-line px-3 py-2">
              <span class="text-[10px] font-bold rounded-full bg-grape-soft text-grape px-2 py-0.5 shrink-0 mt-0.5">
                ${esc(m.kiraciAdi)}
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm truncate">${esc(m.label)}</span>
                <span class="block text-[11px] text-inksoft">
                  ${esc(m.actor?.name ?? "—")} · ${esc(m.entity)} · ${zaman(m.at)}
                </span>
              </span>
            </li>`).join("")}
        </ol>`
        : emptyNote("Henüz kayıtlı değişiklik yok.")}
    </div>`);
}

const zaman = iso => new Date(iso).toLocaleString("tr-TR", {
  day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
});
